import { Injectable, inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { FormKeys, ProjectType } from '@nx-customer-apps/shared/enums';
import { FormOptions, ModernizationAndInsultaionOptions } from '@nx-customer-apps/shared/interfaces';
import { MultiTextbox, Question, TextboxQuestion } from '@nx-customer-apps/shared/utils';
import { ValidatorsService } from '../../../../services/validators.service';
import { FormToolsService } from '../../../../services/form-tools.service';
import { CommonFormService } from '../../../../services/common-form.service';
import { ModernizationsFormService } from '../../modernizations/services';

@Injectable({
    providedIn: 'root'
})
export class ModernizationsAndInsulationFormService {
    private validatorsService = inject(ValidatorsService);
    private formToolsService = inject(FormToolsService);
    private modernizationsForm = inject(ModernizationsFormService);
    private commonFormService = inject(CommonFormService);

    public getModernizationsAndInsulationQuestions(options: ModernizationAndInsultaionOptions): Question[] {
        const insulationsQuestion = this.getInsulations(options);

        if (options.projectType === ProjectType.NewConstruction) {
            return [insulationsQuestion];
        }

        const modernizationsQuestion = this.modernizationsForm.getModernizations(options);

        const renovationAndInsulationQuestions = [
            this.commonFormService.getBuildingConstructionYear(options),
            insulationsQuestion,
            modernizationsQuestion
        ];
        return renovationAndInsulationQuestions;
    }

    private getInsulations(options?: ModernizationAndInsultaionOptions): MultiTextbox {
        const questions = [this.getRoofInsulation(options), this.getUpperFloorInsulation(options), this.getWallsInsulation(options)];

        if (options?.basementPresent) {
            questions.push(this.getBasementInsulation(options));
        }

        return new MultiTextbox({
            question: 'HEATLOAD.MODERNIZATIONS.INSLATIONS.TITLE',
            groupName: FormKeys.Insulations,
            styles: 'flex flex-col',
            questions
        });
    }

    private getRoofInsulation(options?: FormOptions): TextboxQuestion {
        return new TextboxQuestion({
            label: 'COMMON.BUILDING.ROOF',
            floatLabel: 'always',
            key: FormKeys.RoofInsulation,
            value: options?.questions?.[FormKeys.RoofInsulation]?.value || '0',
            styles: 'ca-form-field-s ca-form-field-w-75 vp-form-field-center with-error-message with-wrapped-error-message-450',
            defaultErrorMessage: 'HEATLOAD.MODERNIZATIONS_AND_INSULATION.INSULATION.ERRORS.REQUIRED',
            placeholder: '--',
            validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0, max: 50 })],
            mask: this.formToolsService.getNumericMask({ separatorLimit: '10' }),
            suffix: 'MEASUREMENT_UNITS.CM'
        });
    }

    private getUpperFloorInsulation(options?: FormOptions): TextboxQuestion {
        return new TextboxQuestion({
            label: 'COMMON.BUILDING.UPPER_FLOOR',
            floatLabel: 'always',
            key: FormKeys.UpperFloorInsulation,
            value: options?.questions?.[FormKeys.UpperFloorInsulation]?.value || '0',
            styles: 'ca-form-field-s ca-form-field-w-75 vp-form-field-center with-error-message with-wrapped-error-message-450',
            defaultErrorMessage: 'HEATLOAD.MODERNIZATIONS_AND_INSULATION.INSULATION.ERRORS.REQUIRED',
            placeholder: '--',
            validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0, max: 50 })],
            mask: this.formToolsService.getNumericMask({ separatorLimit: '10' }),
            suffix: 'MEASUREMENT_UNITS.CM'
        });
    }

    private getWallsInsulation(options?: FormOptions): TextboxQuestion {
        return new TextboxQuestion({
            label: 'COMMON.BUILDING.WALL',
            floatLabel: 'always',
            key: FormKeys.WallsInsulation,
            value: options?.questions?.[FormKeys.WallsInsulation]?.value || '0',
            styles: 'ca-form-field-s ca-form-field-w-75 vp-form-field-center with-error-message with-wrapped-error-message-450',
            defaultErrorMessage: 'HEATLOAD.MODERNIZATIONS_AND_INSULATION.INSULATION.ERRORS.REQUIRED',
            placeholder: '--',
            validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0, max: 50 })],
            mask: this.formToolsService.getNumericMask({ separatorLimit: '10' }),
            suffix: 'MEASUREMENT_UNITS.CM'
        });
    }

    private getBasementInsulation(options?: FormOptions): TextboxQuestion {
        return new TextboxQuestion({
            label: 'COMMON.BUILDING.BASEMENT',
            floatLabel: 'always',
            key: FormKeys.BasementInsulation,
            value: options?.questions?.[FormKeys.BasementInsulation]?.value || '0',
            styles: 'ca-form-field-s ca-form-field-w-75 vp-form-field-center with-error-message with-wrapped-error-message-450',
            defaultErrorMessage: 'HEATLOAD.MODERNIZATIONS_AND_INSULATION.INSULATION.ERRORS.REQUIRED',
            placeholder: '--',
            validators: [Validators.required, this.validatorsService.minMaxValidator({ min: 0, max: 50 })],
            mask: this.formToolsService.getNumericMask({ separatorLimit: '10' }),
            suffix: 'MEASUREMENT_UNITS.CM'
        });
    }
}
